import React from 'react';
import { Container, Typography, Box, Accordion, AccordionSummary, AccordionDetails } from '@mui/material';
import { PageHeader } from '../layout/PageHeader';
import { CallToAction } from '../common/CallToAction';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { SEOHead, SEO_PAGES } from '../seo/SEOHead';

export const FAQPage: React.FC = () => {
  const faqs = [
    {
      category: "Subscription & Pricing",
      questions: [
        {
          question: "Can I change plans later?",
          answer: "Yes, you can upgrade or downgrade your plan at any time. Changes will be reflected in your next billing cycle."
        },
        {
          question: "Is there a free trial?",
          answer: "Yes, all plans come with a 14-day free trial and a 7-day money-back guarantee. No credit card required to start your trial."
        },
        {
          question: "What payment methods do you accept?",
          answer: "We accept all major credit cards and PayPal. Enterprise customers can also pay by invoice."
        },
        {
          question: "Can I cancel anytime?",
          answer: "Yes, you can cancel your subscription at any time. No long-term contracts required."
        }
      ]
    },
    {
      category: "Features & Services",
      questions: [
        {
          question: "How does the AI-powered analysis work?",
          answer: "Our AI system analyzes solicitation documents using advanced natural language processing to extract key requirements, deadlines, and evaluation criteria. It then provides structured insights and recommendations based on your business profile."
        },
        {
          question: "Can I integrate GovToolsPro with my existing systems?",
          answer: "Yes, our Enterprise plan includes full API access for custom integrations. You can integrate our analysis capabilities into your existing workflows and systems."
        },
        {
          question: "How accurate is the AI analysis?",
          answer: "Our AI system maintains a high accuracy rate and is continuously improving. We combine multiple AI models and expert-designed algorithms to ensure reliable results, and all analyses can be manually reviewed and adjusted."
        }
      ]
    },
    {
      category: "Technical Support",
      questions: [
        {
          question: "What kind of support do you offer?",
          answer: "We offer different levels of support based on your plan. Basic plans include email support, Professional plans add chat support, and Enterprise plans include 24/7 priority support with a dedicated account manager."
        },
        {
          question: "Do you provide training?",
          answer: "Yes, we provide basic onboarding for all plans. Enterprise plans include personalized training sessions for your team. We also offer regular webinars and maintain a comprehensive knowledge base."
        },
        {
          question: "What is your system uptime?",
          answer: "We maintain a 99.9% uptime guarantee for our services. You can check our status page for real-time system performance metrics."
        }
      ]
    },
    {
      category: "Data & Security",
      questions: [
        {
          question: "How do you handle data security?",
          answer: "We employ industry-standard security measures including encryption at rest and in transit. All data is stored in secure, compliant cloud infrastructure with regular security audits."
        },
        {
          question: "Can I export my data?",
          answer: "Yes, you can export your analysis results, reports, and other data in various formats including PDF, CSV, and JSON. Enterprise users have additional export options through our API."
        }
      ]
    }
  ];

  return (
    <>
      <SEOHead {...SEO_PAGES.faq} />
      <PageHeader
        title="Frequently Asked Questions"
        description="Find answers to common questions about GovToolsPro"
      />
      <Container maxWidth="lg">
        {faqs.map((category, index) => (
          <Box key={index} sx={{ mb: 4 }}>
            <Typography variant="h5" sx={{ mb: 2 }}>
              {category.category}
            </Typography>
            {category.questions.map((faq, faqIndex) => (
              <Accordion key={faqIndex} sx={{ mb: 1 }}>
                <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                  <Typography variant="h6">{faq.question}</Typography>
                </AccordionSummary>
                <AccordionDetails>
                  <Typography>{faq.answer}</Typography>
                </AccordionDetails>
              </Accordion>
            ))}
          </Box>
        ))}

        <CallToAction
          title="Still Have Questions?"
          description="Contact our support team or start your free trial today to experience GovToolsPro firsthand."
          buttonText="Get Started"
          buttonLink="/plans"
        />
      </Container>
    </>
  );
};
